import React from "react";
import { render } from "react-dom";
import { Provider } from "react-redux";
import { asyncWithLDProvider } from "launchdarkly-react-client-sdk";

import { configureEndpoints } from "@/APIClient/endpoints";
import createDashboardStore from "@/state/store";
import App from "./App";
import { configureAirbrake } from "@/util/airbrake";
import {
  getAppName,
  getKubernetesCluster,
  getKubernetesNamespaceAndApp,
} from "@/util/domain";
import { OpenTelemetryGate } from "@/util/opentelemetry";

const main = document.getElementById("main");

if (main == null) {
  throw new Error("No main element!");
} else {
  (async () => {
    // Wait for init.js's config fetch to finish before reading
    // localStorage. `__APP_CONFIG_READY__` is the promise exposed by
    // public/init.js.
    if (window.__APP_CONFIG_READY__) {
      await window.__APP_CONFIG_READY__;
    }

    const storedConfig = localStorage.getItem("revenue-management-config");
    if (typeof storedConfig !== "string") {
      console.error("Config was not fetched, cannot render app.");
      return;
    }

    const config = JSON.parse(storedConfig);
    configureEndpoints(config);
    configureAirbrake(config);
    const store = createDashboardStore(config);
    const launchDarklyID = config?.launchDarkly?.clientSideID ?? "";
    const kubernetesEnvironment = getKubernetesCluster(window.location.hostname);
    const kubernetesNamespace = getKubernetesNamespaceAndApp(
      window.location.hostname
    );

    const LDProvider = await asyncWithLDProvider({
      clientSideID: launchDarklyID,
      options: {
        streaming: true,
        privateAttributes: ["email"],
      },
      reactOptions: {
        useCamelCaseFlagKeys: false,
      },
      context: {
        kind: "multi",
        environment: {
          key: "environment",
          environment: kubernetesEnvironment,
          namespace: kubernetesNamespace,
          hostname: window.location.hostname,
          // Distinguishes apps that share the LD client-side ID; matches App.js identify.
          app: getAppName(window.location.hostname),
        },
      },
    });

    render(
      <LDProvider>
        <OpenTelemetryGate>
          <Provider store={store}>
            <App />
          </Provider>
        </OpenTelemetryGate>
      </LDProvider>,
      main
    );
  })();
}
